import { useState } from 'react';
import { T, Var } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { SystemEntityInline } from '@/components/system-entities';
import { Label } from 'src/components/ui/label';
import {
  entityReferenceCommit,
  entityReferencePicker,
} from './entityReferenceEdit';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import {
  EMPTY_MARK,
  readEntityReference,
  type EntityReferenceValue,
} from './taskTypeViewValue';

type Commit = (value: unknown) => void | Promise<CorrectionCommitResult | void>;

/**
 * Read shows the linked record. Edit searches that record's catalog and
 * writes `{ entityType, entityId }` back onto the correction. Supplier and
 * customer have pickers. Any other entity type still shows the record.
 */
export function TaskTypeViewEntityReference({
  placementId,
  label,
  hideLabel,
  ariaLabel,
  mode,
  referencedEntityType,
  inputValue,
  workingValue,
  onCommit,
}: {
  placementId: string;
  label: string;
  hideLabel?: boolean;
  ariaLabel: string;
  mode: 'read' | 'edit';
  referencedEntityType: SystemEntity | null;
  inputValue: unknown;
  workingValue: unknown;
  onCommit: Commit;
}) {
  const inputRef = readEntityReference(inputValue);
  const workingRef = readEntityReference(workingValue);
  const entityType = referencedEntityType ?? workingRef?.entityType ?? null;

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`task-view-field-${placementId}`}
    >
      {hideLabel ? null : <Label>{label}</Label>}
      {mode === 'read' ? (
        <EntityReferenceValueView reference={inputRef} />
      ) : (
        <EntityReferenceEditor
          entityType={entityType}
          reference={workingRef}
          ariaLabel={ariaLabel}
          onCommit={onCommit}
        />
      )}
      {mode === 'edit' && !sameReference(inputRef, workingRef) ? (
        <p
          className="flex items-center gap-1 text-xs text-muted-foreground"
          data-testid="task-view-original"
        >
          <T context="secondary text under a task view field showing the extracted original value">
            Original:{' '}
            <Var>
              {inputRef ? (
                <EntityReferenceValueView reference={inputRef} />
              ) : (
                EMPTY_MARK
              )}
            </Var>
          </T>
        </p>
      ) : null}
    </div>
  );
}

function EntityReferenceValueView({
  reference,
}: {
  reference: EntityReferenceValue | null;
}) {
  if (!reference) {
    return <span className="text-sm text-muted-foreground">{EMPTY_MARK}</span>;
  }
  return (
    <SystemEntityInline
      entityType={reference.entityType}
      entityId={reference.entityId}
    />
  );
}

function EntityReferenceEditor({
  entityType,
  reference,
  ariaLabel,
  onCommit,
}: {
  entityType: SystemEntity | null;
  reference: EntityReferenceValue | null;
  ariaLabel: string;
  onCommit: Commit;
}) {
  const serverId = reference?.entityId ?? null;
  const [pendingId, setPendingId] = useState<string | null | undefined>(
    undefined,
  );
  if (pendingId !== undefined && pendingId === serverId) {
    setPendingId(undefined);
  }
  const shownId = pendingId === undefined ? serverId : pendingId;

  function select(nextId: string | undefined) {
    const decision = entityReferenceCommit(
      entityType,
      serverId,
      nextId ?? null,
    );
    if (decision.kind === 'skip') return;
    void commitEntityDecision(decision, setPendingId, onCommit);
  }

  return (
    <EntityReferencePicker
      kind={entityReferencePicker(entityType)}
      shownId={shownId ?? undefined}
      ariaLabel={ariaLabel}
      reference={reference}
      onSelect={select}
    />
  );
}

function EntityReferencePicker({
  kind,
  shownId,
  ariaLabel,
  reference,
  onSelect,
}: {
  kind: ReturnType<typeof entityReferencePicker>;
  shownId: string | undefined;
  ariaLabel: string;
  reference: EntityReferenceValue | null;
  onSelect: (nextId: string | undefined) => void;
}) {
  if (kind === 'supplier') {
    return (
      <SupplierAutocomplete
        supplierId={shownId}
        onSelect={onSelect}
        className="w-full"
      />
    );
  }
  if (kind === 'customer') {
    return (
      <CustomersAutocomplete
        selectedCustomerId={shownId}
        onCustomerSelect={onSelect}
        allowClear
        className="w-full"
      />
    );
  }
  return (
    <div aria-label={ariaLabel}>
      <EntityReferenceValueView reference={reference} />
    </div>
  );
}

async function commitEntityDecision(
  decision: Exclude<ReturnType<typeof entityReferenceCommit>, { kind: 'skip' }>,
  setPendingId: (id: string | null | undefined) => void,
  onCommit: Commit,
) {
  const value =
    decision.kind === 'clear'
      ? null
      : { entityType: decision.entityType, entityId: decision.entityId };
  setPendingId(decision.kind === 'clear' ? null : decision.entityId);
  const result = await onCommit(value);
  if (result === 'conflict' || result === 'failed') setPendingId(undefined);
}

function sameReference(
  left: EntityReferenceValue | null,
  right: EntityReferenceValue | null,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.entityType === right.entityType && left.entityId === right.entityId
  );
}
